<template>
  <div ref="rootRef" class="spptbmfb">
    <div
      class="spptbmfb__field"
      :class="{
        'spptbmfb__field--focused': isInputFocused,
        'spptbmfb__field--has-chips': selectedFilters.length > 0,
      }"
      @click="focusInput"
      @mouseenter="isFieldHovered = true"
      @mouseleave="isFieldHovered = false"
    >
      <div class="spptbmfb__leading">
        <img
          :src="require('@components/assets/search-lg.png')"
          alt="search icon"
          class="spptbmfb__search-icon"
        />
      </div>

      <div
        v-for="filter in selectedFilters"
        :key="filter.fieldKey"
        class="spptbmfb__chip"
      >
        <span class="spptbmfb__chip-label">{{ filter.fieldLabel }}:</span>
        <span class="spptbmfb__chip-value">{{ filter.value }}</span>
        <button
          type="button"
          class="spptbmfb__chip-remove"
          aria-label="Remove filter"
          @click="removeFilter(filter.fieldKey)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="8"
            height="8"
            viewBox="0 0 8 8"
            fill="none"
          >
            <path
              d="M6.75 0.75L0.75 6.75M0.75 0.75L6.75 6.75"
              stroke="#98A2B3"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      <input
        ref="searchInputRef"
        v-model="searchText"
        class="spptbmfb__input"
        :placeholder="selectedFilters.length > 0 ? '' : search_instructions"
        @input="onSearchInput"
        @focus="onInputFocus"
        @blur="onInputBlur"
        @keydown="onKeyDown"
      />

      <button
        v-if="(searchText || selectedFilters.length > 0) && (isInputFocused || isFieldHovered)"
        type="button"
        class="spptbmfb__clear-button"
        aria-label="Clear all"
        @mousedown.prevent
        @click="clearAll"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M5 15L15 5M15 15L5 5"
            stroke="#666666"
            stroke-width="1.66667"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <div v-if="shouldShowDropdown" class="spptbmfb__dropdown">
      <div class="spptbmfb__list">
        <div
          v-for="field in filteredFields"
          :key="field.key"
          class="spptbmfb__item"
          :class="{ 'spptbmfb__item--disabled': field.disabled }"
          @mousedown.prevent.stop="!field.disabled && selectField(field)"
          @click.stop="!field.disabled && selectField(field)"
        >
          <div class="spptbmfb__item-content">
            <span class="spptbmfb__item-search">Search</span>
            <span class="spptbmfb__item-label">{{ field.label }}</span>
            <span class="spptbmfb__item-for">for:</span>
            <span class="spptbmfb__item-value">{{ searchText }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  search_instructions: {
    type: String,
    default: "Search for something",
  },
  field_name: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  "filters-changed",
  "filter-added",
  "filter-removed",
  "filters-cleared",
]);

const rootRef = ref(null);
const searchInputRef = ref(null);
const searchText = ref("");
const selectedFilters = ref([]);
const isInputFocused = ref(false);
const isListVisible = ref(false);
const isFieldHovered = ref(false);

const filteredFields = computed(() =>
  props.field_name.map((field) => {
    const isSelected = selectedFilters.value.some(
      (filter) => filter.fieldKey === field.key
    );
    return {
      ...field,
      disabled: isSelected,
    };
  })
);

const shouldShowDropdown = computed(
  () =>
    isListVisible.value &&
    searchText.value &&
    searchText.value.trim().length > 0
);

watch(
  selectedFilters,
  (newFilters) => {
    emit("filters-changed", newFilters);
  },
  { deep: true }
);

function onInputFocus() {
  isInputFocused.value = true;
  isListVisible.value = true;
}

function onInputBlur() {
  setTimeout(() => {
    isInputFocused.value = false;
  }, 200);
}

function focusInput() {
  searchInputRef.value?.focus();
}

function onSearchInput() {
  isListVisible.value = true;
}

function onKeyDown(event) {
  if (event.key === "Enter" && searchText.value.trim() !== "") {
    const firstEnabledField = filteredFields.value.find(
      (field) => !field.disabled
    );
    if (firstEnabledField) {
      selectField(firstEnabledField);
    }
    event.preventDefault();
  }

  if (
    event.key === "Backspace" &&
    searchText.value === "" &&
    selectedFilters.value.length > 0
  ) {
    const lastFilter = selectedFilters.value[selectedFilters.value.length - 1];
    removeFilter(lastFilter.fieldKey);
  }
}

function selectField(field) {
  const trimmedValue = searchText.value.trim();
  if (!trimmedValue) return;

  selectedFilters.value.push({
    fieldKey: field.key,
    fieldLabel: field.label,
    value: trimmedValue,
  });

  searchText.value = "";
  isListVisible.value = false;

  emit("filter-added", {
    fieldKey: field.key,
    fieldLabel: field.label,
    value: trimmedValue,
  });

  setTimeout(() => {
    searchInputRef.value?.focus();
  }, 50);
}

function removeFilter(fieldKey) {
  const index = selectedFilters.value.findIndex(
    (filter) => filter.fieldKey === fieldKey
  );
  if (index === -1) return;

  const removedFilter = selectedFilters.value[index];
  selectedFilters.value.splice(index, 1);
  emit("filter-removed", removedFilter);

  setTimeout(() => {
    searchInputRef.value?.focus();
  }, 50);
}

function clearAll() {
  searchText.value = "";
  selectedFilters.value = [];
  isListVisible.value = false;
  emit("filters-cleared");

  searchInputRef.value?.blur();
}

function reset() {
  searchText.value = "";
  selectedFilters.value = [];
  isListVisible.value = false;
}

function handleClickOutside(event) {
  if (rootRef.value && !rootRef.value.contains(event.target)) {
    isListVisible.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});

defineExpose({
  selectedFilters,
  reset,
});
</script>

<style scoped>
.spptbmfb {
  position: relative;
  width: 100%;
}

.spptbmfb__field {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  transition: all 0.2s ease;
  min-height: 44px;
  padding: 10px 12px;
  cursor: text;
}

.spptbmfb__field:hover {
  border-color: #d6bbfb;
}

.spptbmfb__field--focused {
  border-color: #d6bbfb;
  box-shadow: 0 0 0 4px rgba(127, 86, 217, 0.1);
}

.spptbmfb__field--has-chips {
  padding: 6px 8px;
}

.spptbmfb__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background-color: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 6px;
  font-size: 14px;
  line-height: 20px;
  font-family: Inter, sans-serif;
  white-space: nowrap;
  height: 28px;
}

.spptbmfb__chip-label {
  color: #344054;
  font-weight: 500;
}

.spptbmfb__chip-value {
  color: #475467;
  font-weight: 400;
}

.spptbmfb__chip-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
  width: 16px;
  height: 16px;
  transition: opacity 0.2s ease;
}

.spptbmfb__chip-remove:hover {
  opacity: 0.7;
}

.spptbmfb__chip-remove img {
  width: 16px;
  height: 16px;
}

.spptbmfb__leading {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 28px;
}

.spptbmfb__search-icon {
  width: 20px;
  height: 20px;
}

.spptbmfb__input {
  flex: 1;
  width: 100%;
  min-width: 120px;
  border: none;
  outline: none;
  background: transparent;
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: #101828;
  padding: 0;
  height: 28px;
}

.spptbmfb__input::placeholder {
  color: #101828;
  opacity: 0.2;
}

.spptbmfb__clear-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  transition: opacity 0.2s ease;
  line-height: 0;
  position: absolute;
  right: 10px;
}

.spptbmfb__clear-button:hover {
  opacity: 0.7;
}

.spptbmfb__clear-button svg {
  width: 18px;
  height: 18px;
  display: block;
}

.spptbmfb__dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  width: 100%;
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #eaecf0;
  box-shadow: 0px 12px 16px 4px rgba(16, 24, 40, 0.08);
  z-index: 1000;
  overflow: hidden;
}

.spptbmfb__list {
  max-height: 280px;
  overflow-y: auto;
  padding: 4px;
}

.spptbmfb__list::-webkit-scrollbar {
  width: 8px;
}

.spptbmfb__list::-webkit-scrollbar-track {
  background-color: #fff;
  border-radius: 10px;
}

.spptbmfb__list::-webkit-scrollbar-thumb {
  background-color: #eaecf0;
  border-radius: 10px;
}

.spptbmfb__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.spptbmfb__item:hover {
  background-color: #f9fafb;
}

.spptbmfb__item--disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: transparent !important;
}

.spptbmfb__item-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.spptbmfb__item-content {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
}

.spptbmfb__item-label {
  font-weight: 500;
  color: #344054;
}

.spptbmfb__item-for {
  font-weight: 400;
  color: #98a2b3;
}

.spptbmfb__item-search {
  font-weight: 400;
  color: #98a2b3;
}

.spptbmfb__item-value {
  font-weight: 400;
  color: #475467;
}
</style>
